import type {
  IntegrationCandidateEnvelope,
  IntegrationReviewAction,
} from "@/src/features/integrations/integrationReview";

/**
 * An integration review, decided from its Notifications card.
 *
 * The card shows words, never the signed owner command: that command names
 * the candidate digest and exists for the chat entrance, where the owner has
 * to repeat it exactly. The card carries the review ID instead, and the review
 * route rebuilds the command on the server from the live candidate. This
 * module stays client-safe: it imports no server code and no `node:crypto`.
 */
export const INTEGRATION_REVIEW_NOTIFICATION_TYPE = "integration_review";

/**
 * Domains whose cards can decide. The ones with no account binding resolve
 * from (domain, provider) alone. CRM reviews resolve from their stored task.
 * Account-bound music and every entertainment review need a binding the card
 * cannot hold, so they keep their Settings and chat paths. The values are
 * repeated here to keep server modules out of the client bundle; a test pins
 * them to the server constants.
 */
export const PROVIDER_LEVEL_CARD_DECISION_DOMAINS = [
  "email",
  "human_resources",
  "developer_source_review",
  "music",
] as const;
export const CRM_CARD_DECISION_DOMAIN = "crm";

export function canDecideIntegrationReviewFromCard(
  candidate: Pick<IntegrationCandidateEnvelope, "domain" | "binding">,
): boolean {
  if (candidate.domain === CRM_CARD_DECISION_DOMAIN) return true;
  return (PROVIDER_LEVEL_CARD_DECISION_DOMAINS as readonly string[]).includes(candidate.domain)
    && candidate.binding.accountKey === null;
}

/** What the card stores and posts: the review and the answers still open. */
export type IntegrationReviewCard = {
  reviewId: string;
  actions: IntegrationReviewAction[];
};

const REVIEW_ID = /^integration-review:([a-z0-9][a-z0-9._-]{0,127}):([a-z0-9][a-z0-9._-]{0,127}):([a-f0-9]{32})$/;

/** The parts of a review ID (the review task's job ID), or null when malformed. */
export function parseIntegrationReviewId(
  reviewId: string,
): { domain: string; provider: string; candidateId: string } | null {
  const match = REVIEW_ID.exec(reviewId);
  return match ? { domain: match[1], provider: match[2], candidateId: match[3] } : null;
}

const CARD_ACTIONS: readonly IntegrationReviewAction[] = ["approve", "reject", "finish_setup"];

const record = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : null;

/**
 * The decision on a feed row, or null when the row is not a decidable review.
 * A feed row carries its stored extras lifted onto the row; the widget's
 * items (`buildNotificationFeedItems`) and client-built rows nest them under
 * `extraFields`. Both are read.
 */
export function readIntegrationReviewCard(item: {
  notificationType?: unknown;
  extraFields?: unknown;
  integrationReview?: unknown;
}): IntegrationReviewCard | null {
  if (item.notificationType !== INTEGRATION_REVIEW_NOTIFICATION_TYPE) return null;
  const review = record(record(item.extraFields)?.integrationReview ?? item.integrationReview);
  return readIntegrationReviewDecision(review?.ownerDecision);
}

/**
 * A stored `ownerDecision`, read by field: key order and action order do not
 * matter (a sealed row reads its fields back in sorted key order). Null when
 * it is missing or was not written by this code.
 */
export function readIntegrationReviewDecision(value: unknown): IntegrationReviewCard | null {
  const decision = record(value);
  if (!decision || typeof decision.reviewId !== "string" || !parseIntegrationReviewId(decision.reviewId)) return null;
  const listed: unknown = decision.actions;
  if (!Array.isArray(listed) || listed.length === 0) return null;
  const actions = CARD_ACTIONS.filter((action) => listed.includes(action));
  // Unknown or repeated answers mean the card was not written by this code.
  if (actions.length !== listed.length) return null;
  return { reviewId: decision.reviewId, actions };
}

/** True when two stored decisions name the same review and the same answers. */
export function sameIntegrationReviewDecision(left: unknown, right: unknown): boolean {
  const a = readIntegrationReviewDecision(left);
  const b = readIntegrationReviewDecision(right);
  if (!a || !b) return a === b;
  return a.reviewId === b.reviewId
    && a.actions.length === b.actions.length
    && a.actions.every((action, index) => action === b.actions[index]);
}

/** The verbs, in the words the card uses (also the i18n sources). */
export const INTEGRATION_REVIEW_VERBS: Record<IntegrationReviewAction, { idle: string; busy: string }> = {
  approve: { idle: "Approve", busy: "Approving…" },
  reject: { idle: "Decline", busy: "Declining…" },
  finish_setup: { idle: "Finish setup", busy: "Finishing setup…" },
};

/** The review route, as `makePostRequest` takes it (relative to `/api/`). */
export const INTEGRATION_REVIEW_DECISION_PATH = "platform-integrations/review";

export type IntegrationReviewPost = (path: string, body: unknown, signal?: AbortSignal) => Promise<unknown>;

/** What the card says when an answer did not go through (also the i18n sources). */
export const INTEGRATION_REVIEW_DECISION_MESSAGES = {
  settled: "This isn't waiting on you anymore, so nothing changed.",
  unauthorized: "Sign in again to answer this.",
  setupIncomplete: "Setup isn't done yet, so nothing changed.",
  failed: "That didn't go through, so nothing changed. Try again.",
} as const;

/**
 * A decision that did not go through, in words the card can show. `settled`
 * means the review no longer waits on this card (decided elsewhere, or
 * replaced by a newer review), so the card should go.
 */
export class IntegrationReviewDecisionError extends Error {
  constructor(readonly code: string, message: string, readonly settled = false) {
    super(message);
    this.name = "IntegrationReviewDecisionError";
  }
}

const isAbort = (error: unknown) => error instanceof DOMException && error.name === "AbortError";

/**
 * Send the owner's answer to the review route. Resolves once the route
 * applied it; throws {@link IntegrationReviewDecisionError} otherwise.
 */
export async function decideIntegrationReview(
  card: IntegrationReviewCard,
  action: IntegrationReviewAction,
  { post, signal }: { post: IntegrationReviewPost; signal?: AbortSignal },
): Promise<void> {
  if (!card.actions.includes(action)) {
    throw new IntegrationReviewDecisionError("settled", INTEGRATION_REVIEW_DECISION_MESSAGES.settled, true);
  }
  try {
    await post(INTEGRATION_REVIEW_DECISION_PATH, { reviewId: card.reviewId, action }, signal);
  } catch (error) {
    if (isAbort(error)) throw error;
    const failure = error && typeof error === "object" ? (error as { status?: unknown; body?: unknown }) : {};
    const body = record(failure.body);
    const code = String(body?.code ?? "");
    if (failure.status === 401 || failure.status === 403) {
      throw new IntegrationReviewDecisionError("unauthorized", INTEGRATION_REVIEW_DECISION_MESSAGES.unauthorized);
    }
    if (body?.settled === true) {
      throw new IntegrationReviewDecisionError(code || "settled", INTEGRATION_REVIEW_DECISION_MESSAGES.settled, true);
    }
    if (code === "setup_incomplete") {
      throw new IntegrationReviewDecisionError(code, INTEGRATION_REVIEW_DECISION_MESSAGES.setupIncomplete);
    }
    throw new IntegrationReviewDecisionError(code || "network_error", INTEGRATION_REVIEW_DECISION_MESSAGES.failed);
  }
}
